// ============================================================
// TATHYA DMS - New Database Creation Form
// Version: 1.0.0
// Date: 2026-04-18
// ============================================================

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createDatabase } from '../../services/api';

const DATA_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency' },
  { value: 'date', label: 'Date' },
  { value: 'time', label: 'Time (clock — e.g. 9:30 AM)' },
  { value: 'hour', label: 'Duration (hours/minutes — e.g. 1:58)' },
  { value: 'percentage', label: 'Percentage' },
  { value: 'formula', label: 'Formula (calculated)' },
  { value: 'phone', label: 'Phone Number (10 digits)' },
  { value: 'email', label: 'Email Address' }
];

const NewDatabase = () => {
  const { org_slug } = useParams();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [columns, setColumns] = useState([
    { label: '', data_type: 'text' }
  ]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const addColumn = () => {
    setColumns([...columns, { label: '', data_type: 'text' }]);
  };

  const removeColumn = (index) => {
    setColumns(columns.filter((_, i) => i !== index));
  };

  const updateColumn = (index, field, value) => {
    const updated = [...columns];
    updated[index][field] = value;
    setColumns(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      return setError('Database name is required');
    }

    const validColumns = columns.filter(c => c.label.trim());
    if (validColumns.length === 0) {
      return setError('At least one column is required');
    }

    // Normalise each column payload:
    //   - formula columns: keep formula_expression (empty => error)
    //   - text columns: parse comma-separated allowed_values into a
    //     JSON array (matches how ColumnManager stores it). Empty
    //     string => no restriction, pass null.
    //   - all other types: strip both fields to keep payload clean.
    // (Apr 24 — Item 22.)
    const payloadColumns = [];
    for (const c of validColumns) {
      const col = { label: c.label.trim(), data_type: c.data_type };
      if (c.data_type === 'formula') {
        const expr = (c.formula_expression || '').trim();
        if (!expr) {
          return setError(`Column "${col.label}" is a formula column — please enter its expression.`);
        }
        col.formula_expression = expr;
      } else if (c.data_type === 'text') {
        const raw = (c.allowed_values || '').trim();
        if (raw) {
          const opts = raw.split(',').map(s => s.trim()).filter(Boolean);
          if (opts.length > 0) col.allowed_values = JSON.stringify(opts);
        }
      }
      payloadColumns.push(col);
    }

    setLoading(true);
    try {
      const res = await createDatabase(org_slug, {
        name: name.trim(),
        columns: payloadColumns
      });
      navigate(`/${org_slug}/databases/${res.data.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create database');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Create New Database</h1>
          <p style={styles.subtitle}>
            Define your database name and columns
          </p>
        </div>
        <button
          className="btn btn-secondary"
          onClick={() => navigate(`/${org_slug}/dashboard`)}
        >
          Cancel
        </button>
      </div>

      <div className="card" style={styles.card}>
        <form onSubmit={handleSubmit}>

          {/* Database Name */}
          <div className="form-group">
            <label>Database Name *</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Vendor Contract Register"
              required
              autoFocus
              style={{ fontSize: '16px', padding: '12px' }}
            />
            <p style={styles.hint}>
              A unique number will be auto-assigned (e.g. 2026-DB-0000001)
            </p>
          </div>

          {/* Columns */}
          <div style={styles.columnsSection}>
            <div style={styles.columnHeader}>
              <h3 style={styles.columnTitle}>Define Columns</h3>
              <button
                type="button"
                className="btn btn-primary"
                onClick={addColumn}
                style={{ padding: '6px 14px', fontSize: '13px' }}
              >
                + Add Column
              </button>
            </div>

            {/* Column list */}
            <div style={styles.columnList}>
              {/* Header row */}
              <div style={styles.columnRow}>
                <div style={styles.colNum}>#</div>
                <div style={styles.colLabel}>Column Name *</div>
                <div style={styles.colType}>Data Type</div>
                <div style={styles.colAction}></div>
              </div>

              {columns.map((col, index) => (
                <React.Fragment key={index}>
                  <div style={styles.columnRow}>
                    <div style={{...styles.colNum, color: '#2E75B6', fontWeight: '600'}}>
                      {index + 1}
                    </div>
                    <div style={styles.colLabel}>
                      <input
                        type="text"
                        value={col.label}
                        onChange={e => updateColumn(index, 'label', e.target.value)}
                        placeholder={`Column ${index + 1} name`}
                        style={styles.colInput}
                      />
                    </div>
                    <div style={styles.colType}>
                      <select
                        value={col.data_type}
                        onChange={e => updateColumn(index, 'data_type', e.target.value)}
                        style={styles.colSelect}
                      >
                        {DATA_TYPES.map(dt => (
                          <option key={dt.value} value={dt.value}>
                            {dt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={styles.colAction}>
                      {columns.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeColumn(index)}
                          style={styles.removeBtn}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>

                  {/* ── Per-type config row (Apr 24 — Item 22) ──
                      Formula columns need an expression. Text columns
                      may optionally restrict to a dropdown via
                      allowed_values. All other types need no extra
                      config at creation time. */}
                  {col.data_type === 'formula' && (
                    <div style={styles.configRow}>
                      <div style={styles.configLabel}>Formula expression</div>
                      <div style={styles.configBody}>
                        <input
                          type="text"
                          value={col.formula_expression || ''}
                          onChange={e => updateColumn(index, 'formula_expression', e.target.value)}
                          placeholder="e.g. [Product Price]*[GST]+[Product Price]"
                          style={styles.colInput}
                        />
                        {/* Insert-column helper: click to append [Name] */}
                        <div style={styles.insertHelpers}>
                          <span style={styles.helperLabel}>Insert:</span>
                          {columns
                            .filter((c, i) => i !== index && c.label.trim() && c.data_type !== 'formula' && c.data_type !== 'pdf')
                            .map((c, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() => {
                                  const cur = col.formula_expression || '';
                                  updateColumn(index, 'formula_expression', cur + `[${c.label.trim()}]`);
                                }}
                                style={styles.helperBtn}
                                title={`Insert [${c.label.trim()}] into the formula`}
                              >
                                +[{c.label.trim()}]
                              </button>
                            ))}
                          {columns.filter((c, i) => i !== index && c.label.trim() && c.data_type !== 'formula' && c.data_type !== 'pdf').length === 0 && (
                            <span style={styles.helperHint}>
                              Add other columns above to reference them here.
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {col.data_type === 'text' && (
                    <div style={styles.configRow}>
                      <div style={styles.configLabel}>Allowed values</div>
                      <div style={styles.configBody}>
                        <input
                          type="text"
                          value={col.allowed_values || ''}
                          onChange={e => updateColumn(index, 'allowed_values', e.target.value)}
                          placeholder="Optional. Comma-separated, e.g. YES, NO, MAYBE"
                          style={styles.colInput}
                        />
                        <p style={styles.configHint}>
                          If set, the cell becomes a dropdown limited to these options. Leave blank for free text.
                        </p>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {error && (
            <p className="error-msg" style={{ marginTop: '16px' }}>
              {error}
            </p>
          )}

          {/* Submit */}
          <div style={styles.actions}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(`/${org_slug}/dashboard`)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
              style={{ padding: '10px 32px' }}
            >
              {loading ? 'Creating...' : 'Create Database'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px'
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1B3A5C'
  },
  subtitle: {
    color: '#595959',
    fontSize: '14px',
    marginTop: '4px'
  },
  card: {
    maxWidth: '800px'
  },
  hint: {
    fontSize: '12px',
    color: '#595959',
    marginTop: '6px'
  },
  columnsSection: {
    marginTop: '24px'
  },
  columnHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px'
  },
  columnTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1B3A5C'
  },
  columnList: {
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    overflow: 'hidden'
  },
  columnRow: {
    display: 'grid',
    gridTemplateColumns: '40px 1fr 160px 40px',
    gap: '0',
    borderBottom: '1px solid #f0f0f0',
    alignItems: 'center'
  },
  colNum: {
    padding: '10px',
    textAlign: 'center',
    fontSize: '13px',
    color: '#595959',
    background: '#fafafa',
    borderRight: '1px solid #f0f0f0'
  },
  colLabel: {
    padding: '6px 8px',
    borderRight: '1px solid #f0f0f0'
  },
  colType: {
    padding: '6px 8px',
    borderRight: '1px solid #f0f0f0'
  },
  colAction: {
    padding: '6px',
    textAlign: 'center'
  },
  colInput: {
    width: '100%',
    padding: '6px 8px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '13px',
    outline: 'none'
  },
  colSelect: {
    width: '100%',
    padding: '6px 8px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '13px',
    outline: 'none',
    background: 'white'
  },
  removeBtn: {
    background: 'none',
    border: 'none',
    color: '#C0392B',
    cursor: 'pointer',
    fontSize: '14px',
    padding: '4px'
  },
  // ── Per-type config row (Item 22) ──
  configRow: {
    display: 'grid',
    gridTemplateColumns: '40px 160px 1fr',
    gap: '0',
    borderBottom: '1px solid #f0f0f0',
    background: '#FAFBFC',
    alignItems: 'flex-start'
  },
  configLabel: {
    gridColumn: '2 / 3',
    padding: '10px 8px',
    fontSize: '12px',
    color: '#595959',
    fontWeight: '500'
  },
  configBody: {
    padding: '8px 8px 10px 8px'
  },
  insertHelpers: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    marginTop: '6px',
    alignItems: 'center'
  },
  helperLabel: {
    fontSize: '11px',
    color: '#595959',
    marginRight: '4px'
  },
  helperBtn: {
    background: '#EBF3FB',
    border: '1px solid #B8D4E8',
    color: '#1B3A5C',
    padding: '2px 8px',
    fontSize: '11px',
    borderRadius: '3px',
    cursor: 'pointer'
  },
  helperHint: {
    fontSize: '11px',
    color: '#9c9c9c',
    fontStyle: 'italic'
  },
  configHint: {
    fontSize: '11px',
    color: '#595959',
    marginTop: '4px'
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '24px',
    paddingTop: '20px',
    borderTop: '1px solid #f0f0f0'
  }
};

export default NewDatabase;
